import { z } from 'zod';

import ApiError from '~/types/ApiError';

import { request } from './common';

// The same public calendar svc-bundle's availability monitor reads.
const calendarSchema = z.object({
  result: z.array(
    z.object({
      packageId: z.string(),
      duration: z.number(),
      maps: z.array(z.array(z.string())),
      rates: z.array(z.string()),
      dates: z.record(z.string(), z.array(z.array(z.unknown()))),
    }),
  ),
});

export interface FlashPackageStay {
  offerId: string;
  /** Salesforce package id. */
  packageId: string;
  nights: number;
}

function calendarUrl(stay: FlashPackageStay, occupancy: number, minDate?: string): string {
  const query = new URLSearchParams({
    region: 'AU',
    currencyCode: 'AUD',
    'nights[0]': String(stay.nights),
    'occupancy[0]': String(occupancy),
    packageIds: stay.packageId,
    enquiryType: 'customer',
    brand: 'luxuryescapes',
  });
  if (minDate) {
    query.set('minDate', minDate);
  }
  return `${window.configs.API_HOST}/api/v2/calendar/${stay.offerId}/rates?${query.toString()}`;
}

function nextDay(date: string): string {
  const next = new Date(`${date}T00:00:00.000Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

export interface FlashStayRate {
  rooms: number;
  /** Whole AUD for the room's stay; null when no price is published, e.g. when it's sold out. */
  price: number | null;
}

/**
 * Rooms left and price by check-in date for one occupancy of a flash package stay. The calendar
 * returns a window of dates at a time, so later windows are requested until every check-in is
 * covered. A check-in the calendar doesn't sell is left out.
 */
export async function getStayRates(
  stay: FlashPackageStay,
  occupancy: number,
  checkInDates: Array<string>,
  options?: { signal?: AbortSignal },
): Promise<Record<string, FlashStayRate>> {
  const wanted = new Set(checkInDates);
  const lastWanted = [...wanted].sort().pop();
  const rates: Record<string, FlashStayRate> = {};
  let minDate: string | undefined;

  while (lastWanted) {
    const response = await request(calendarUrl(stay, occupancy, minDate), {
      method: 'GET',
      signal: options?.signal,
    }).catch((error: unknown) => {
      // The calendar reads the public offer, which only exists while the hotel is on sale.
      if (error instanceof ApiError && error.status === 404) {
        throw new Error("the hotel isn't on sale in AU right now, so the calendar has no rates for it.");
      }
      throw error;
    });
    const calendar = calendarSchema
      .parse(response)
      .result.find((item) => item.packageId === stay.packageId && item.duration === stay.nights);
    if (!calendar) break;

    // Rows are compressed: each uses the field map with the same number of entries.
    const mappings = new Map(calendar.maps.map((map) => [map.length, map] as const));
    Object.entries(calendar.dates).forEach(([checkIn, rows]) => {
      // The first row is the package's first room rate, as the monitors read it.
      const row = rows[0];
      const map = row ? mappings.get(row.length) : undefined;
      const rooms = map ? row[map.indexOf('availableRooms')] : undefined;
      if (!wanted.has(checkIn) || typeof rooms !== 'number') return;
      // One room was asked for, so its price is the first entry.
      const prices = map.includes('prices') ? row[map.indexOf('prices')] : undefined;
      const price = Array.isArray(prices) && typeof prices[0] === 'number' ? prices[0] : null;
      rates[checkIn] = { rooms, price };
    });

    const highest = Object.keys(calendar.dates).sort().pop();
    // An empty or repeated window cannot move forward, so stop rather than loop.
    if (!highest || highest >= lastWanted || (minDate && highest < minDate)) break;
    minDate = nextDay(highest);
  }

  return rates;
}
